import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import {
  BCG_HEIGHT,
  BCG_WIDTH,
  type BcgLayout,
  type Quadrant,
} from "@/lib/tool-kit/business-templates/bcg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/** Quadrant tints and bubble colours (Okabe–Ito), so the picture survives greyscale. */
const TINT: Record<Quadrant, string> = {
  star: "#fff6dc",
  question: "#eaf4fb",
  cow: "#e8f5ee",
  dog: "#f3f3f3",
};
const BUBBLE: Record<Quadrant, string> = {
  star: CHART_COLORS[1],
  question: CHART_COLORS[4],
  cow: CHART_COLORS[2],
  dog: "#8a8a8a",
};

export interface BcgChartText {
  title: string;
  xTitle: string;
  yTitle: string;
  quadrants: Record<Quadrant, string>;
  xTick: (value: number) => string;
  yTick: (value: number) => string;
  footer: readonly string[];
  label: string;
  description: string;
}

/** The growth-share matrix as a self-contained SVG, drawn from `layoutBcg`. */
export function BcgChart({
  id,
  layout,
  text,
}: {
  id: string;
  layout: BcgLayout;
  text: BcgChartText;
}) {
  const { plot, shareX, growthY } = layout;
  const regions: {
    quadrant: Quadrant;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
  }[] = [
    { quadrant: "star", x0: plot.left, x1: shareX, y0: plot.top, y1: growthY },
    {
      quadrant: "question",
      x0: shareX,
      x1: plot.right,
      y0: plot.top,
      y1: growthY,
    },
    {
      quadrant: "cow",
      x0: plot.left,
      x1: shareX,
      y0: growthY,
      y1: plot.bottom,
    },
    {
      quadrant: "dog",
      x0: shareX,
      x1: plot.right,
      y0: growthY,
      y1: plot.bottom,
    },
  ];
  const corner = (quadrant: Quadrant) => {
    const region = regions.find((r) => r.quadrant === quadrant);
    const left = quadrant === "star" || quadrant === "cow";
    const top = quadrant === "star" || quadrant === "question";
    return {
      x: left ? (region?.x0 ?? 0) + 8 : (region?.x1 ?? 0) - 8,
      y: top ? (region?.y0 ?? 0) + 8 : (region?.y1 ?? 0) - 26,
      anchor: left ? ("start" as const) : ("end" as const),
    };
  };
  return (
    <ChartSvg
      description={text.description}
      height={BCG_HEIGHT}
      id={id}
      label={text.label}
      width={BCG_WIDTH}
    >
      <TextLines
        anchor="middle"
        bold
        fontSize={18}
        lineHeight={24}
        lines={[text.title]}
        top={14}
        x={BCG_WIDTH / 2}
      />
      {regions.map((region) => (
        <rect
          fill={TINT[region.quadrant]}
          height={Math.max(0, region.y1 - region.y0)}
          key={region.quadrant}
          width={Math.max(0, region.x1 - region.x0)}
          x={region.x0}
          y={region.y0}
        />
      ))}
      {layout.xTicks.map((tick) => (
        <g key={`x-${tick.value}`}>
          <line
            stroke={CHART_INK.grid}
            x1={tick.x}
            x2={tick.x}
            y1={plot.top}
            y2={plot.bottom}
          />
          <TextLines
            anchor="middle"
            fill={CHART_INK.text}
            fontSize={12}
            lineHeight={16}
            lines={[text.xTick(tick.value)]}
            top={plot.bottom + 6}
            x={tick.x}
          />
        </g>
      ))}
      {layout.yTicks.map((tick) => (
        <g key={`y-${tick.value}`}>
          <line
            stroke={CHART_INK.grid}
            x1={plot.left}
            x2={plot.right}
            y1={tick.y}
            y2={tick.y}
          />
          <TextLines
            anchor="end"
            fill={CHART_INK.text}
            fontSize={12}
            lineHeight={16}
            lines={[text.yTick(tick.value)]}
            top={tick.y - 8}
            x={plot.left - 8}
          />
        </g>
      ))}
      <rect
        fill="none"
        height={plot.bottom - plot.top}
        stroke={CHART_INK.axis}
        width={plot.right - plot.left}
        x={plot.left}
        y={plot.top}
      />
      <line
        stroke={CHART_INK.text}
        strokeDasharray="6 4"
        strokeWidth={1.5}
        x1={shareX}
        x2={shareX}
        y1={plot.top}
        y2={plot.bottom}
      />
      <line
        stroke={CHART_INK.text}
        strokeDasharray="6 4"
        strokeWidth={1.5}
        x1={plot.left}
        x2={plot.right}
        y1={growthY}
        y2={growthY}
      />
      {(["star", "question", "cow", "dog"] as const).map((quadrant) => {
        const position = corner(quadrant);
        return (
          <TextLines
            anchor={position.anchor}
            bold
            fill={CHART_INK.muted}
            fontSize={15}
            key={quadrant}
            lineHeight={18}
            lines={[text.quadrants[quadrant]]}
            top={position.y}
            x={position.x}
          />
        );
      })}
      {[...layout.bubbles]
        .sort((a, b) => b.r - a.r)
        .map((bubble) => (
          <g key={bubble.index}>
            <circle
              cx={bubble.x}
              cy={bubble.y}
              fill={BUBBLE[bubble.quadrant]}
              fillOpacity={0.78}
              r={bubble.r}
              stroke={CHART_INK.text}
              strokeWidth={1}
            />
            <TextLines
              anchor={bubble.label.anchor}
              fill={CHART_INK.text}
              fontSize={11}
              lineHeight={14}
              lines={[bubble.label.text]}
              top={bubble.label.y - 7}
              x={bubble.label.x}
            />
          </g>
        ))}
      <TextLines
        anchor="middle"
        fill={CHART_INK.text}
        fontSize={13}
        lineHeight={16}
        lines={[text.xTitle]}
        top={plot.bottom + 28}
        x={(plot.left + plot.right) / 2}
      />
      <g transform={`rotate(-90 20 ${(plot.top + plot.bottom) / 2})`}>
        <TextLines
          anchor="middle"
          fill={CHART_INK.text}
          fontSize={13}
          lineHeight={16}
          lines={[text.yTitle]}
          top={(plot.top + plot.bottom) / 2 - 8}
          x={20}
        />
      </g>
      <TextLines
        anchor="middle"
        fill={CHART_INK.muted}
        fontSize={11}
        lineHeight={14}
        lines={text.footer}
        top={BCG_HEIGHT - 36}
        x={BCG_WIDTH / 2}
      />
    </ChartSvg>
  );
}
